'use client';

import { Clock, CreditCard, Ticket, Users, Video } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { formatCurrency } from '@ui/lib/utils';
import { useActivityAvailabilitySummary } from '@website/hooks/useActivityAvailabilitySummary';
import type { ActivityAvailabilitySummary } from '@website/services/activities.service';

interface ActivityAvailabilityIslandProps {
  activityId: string;
  locale: string;
  freeLabel: string;
  /**
   * Optional pre-resolved summary (the owner preview renders a static node built
   * from the in-progress activity, so it passes the values directly instead of
   * fetching). When omitted, the island fetches live on mount.
   */
  staticSummary?: ActivityAvailabilitySummary;
}

interface AvailabilityRowsProps {
  summary: ActivityAvailabilitySummary;
  locale: string;
  freeLabel: string;
}

/** Humanize a minute count as `2h`, `1h 30m` or `45m`. */
const formatDuration = (minutes: number): string => {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest > 0 ? `${hours}h ${rest}m` : `${hours}h`;
  }
  return `${minutes}m`;
};

/** The resolved live rows: from-price + slot types + payment + online. */
function AvailabilityRows({ summary, locale, freeLabel }: AvailabilityRowsProps) {
  const t = useTranslations('ActivityDetail');
  const tActivityCard = useTranslations('ActivityCard');

  const chips: { icon: React.ReactNode; text: string }[] = [];

  // Duration: the live bookable-session length range across future-open
  // occurrences (RANGE contributes its min-bookable unit..window). Same i18n keys
  // and Clock icon the SSR meta row used before this moved into the live island.
  if (summary.durationRange) {
    const { min, max } = summary.durationRange;
    chips.push({
      icon: <Clock className="w-4 h-4" />,
      text:
        min === max
          ? t('slotDurationFrom', { duration: formatDuration(min) })
          : t('slotDurationRange', { min: formatDuration(min), max: formatDuration(max) })
    });
  }

  // Group size: the live head-count range across future-open GROUP occurrences.
  // Same i18n keys and Users icon the SSR meta row used.
  if (summary.groupSizeRange) {
    const { min, max } = summary.groupSizeRange;
    chips.push({
      icon: <Users className="w-4 h-4" />,
      text: min === max ? t('groupSizeUpTo', { count: max }) : t('groupSizeRange', { min, max })
    });
  }

  // Slot types: both -> combined, one -> that one, neither -> hidden.
  if (summary.hasSingle && summary.hasGroup) {
    chips.push({ icon: <Ticket className="w-4 h-4" />, text: t('singleGroupSlots') });
  } else if (summary.hasSingle) {
    chips.push({ icon: <Ticket className="w-4 h-4" />, text: t('singleSlots') });
  } else if (summary.hasGroup) {
    chips.push({ icon: <Ticket className="w-4 h-4" />, text: t('groupSlots') });
  }

  // Online (visio) availability indicator.
  if (summary.hasOnline) {
    chips.push({ icon: <Video className="w-4 h-4" />, text: t('onlineAvailable') });
  }

  // Accepted payments.
  const paymentLabels: string[] = [];
  if (summary.acceptsCash) paymentLabels.push(t('paymentCash'));
  if (summary.acceptsOnline) paymentLabels.push(t('paymentOnline'));
  if (paymentLabels.length > 0) {
    chips.push({
      icon: <CreditCard className="w-4 h-4" />,
      text: t('acceptedPayments', { methods: paymentLabels.join(', ') })
    });
  }

  const price = summary.minOpenPrice;

  return (
    <div className="flex flex-col gap-3">
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-neutral-600">
          {chips.map((chip, index) => (
            <div key={index} className="inline-flex items-center gap-1.5">
              <span className="text-neutral-400 shrink-0">{chip.icon}</span>
              <span>{chip.text}</span>
            </div>
          ))}
        </div>
      )}

      {price != null && (
        <div className="flex items-baseline gap-1.5">
          {price > 0 && <span className="text-sm text-neutral-500 capitalize">{tActivityCard('onwards')}</span>}
          <span className="text-primary text-xl md:text-2xl font-semibold">
            {formatCurrency(price, 'EUR', locale === 'fr' ? 'fr-FR' : 'en-US', freeLabel)}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Reserved-space skeleton matched to the final content footprint (one chip row +
 * the price line) so the SSR shell holds the space and hydration shifts almost
 * nothing. Same vertical rhythm (`gap-3`) and type sizes as the resolved rows.
 */
function AvailabilitySkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-hidden="true">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="h-4 w-32 rounded bg-neutral-100 animate-pulse" />
        <span className="h-4 w-28 rounded bg-neutral-100 animate-pulse" />
      </div>
      <div className="h-7 md:h-8 w-24 rounded bg-neutral-100 animate-pulse" />
    </div>
  );
}

/**
 * The LIVE availability island for the public activity-detail header. The detail
 * page is SSR-cached for static catalog content; the bookable signals (from-price,
 * slot types, payment, online) are fetched here on mount so they always reflect
 * live availability. Renders a reserved-space skeleton until resolved (near-zero
 * CLS), then the resolved rows. An error / empty result collapses gracefully to
 * the skeleton footprint's place by rendering nothing.
 */
export function ActivityAvailabilityIsland({
  activityId,
  locale,
  freeLabel,
  staticSummary
}: ActivityAvailabilityIslandProps) {
  const { data, isLoading, isError } = useActivityAvailabilitySummary(staticSummary ? undefined : activityId);
  const summary = staticSummary ?? data;

  // While the live fetch is in flight, reserve the footprint with a skeleton so
  // hydration shifts almost nothing. On error, collapse silently (the booking
  // flow still works; the header just omits the live signals).
  if (!summary) {
    if (staticSummary === undefined && (isLoading || (!isError && data === undefined))) {
      return <AvailabilitySkeleton />;
    }
    return null;
  }

  return <AvailabilityRows summary={summary} locale={locale} freeLabel={freeLabel} />;
}
